export type FormElemType =
  | "sect"
  | "str"
  | "text"
  | "bool"
  | "select"
  | "num"
  | "signature"
  | "date";

export type FormElem = {
  id: string;
  type: FormElemType;
  name?: string;
  desc?: string;
  placeholder?: string;
  required?: boolean;
  unit?: string;
  options?: { val: string; dispval?: string }[];
  children?: FormElem[];
};

export type FormDocument = {
  name: string;
  desc: string;
  role: string;
  parent: string;
  children: FormElem[];
};

const COMMON_FIELDS = [
  { key: "str", title: "Short text", icon: "fa-font" },
  { key: "date", title: "Date", icon: "fa-calendar" },
  { key: "str", title: "Email", icon: "fa-envelope" },
  { key: "str", title: "Phone", icon: "fa-phone" },
] as const;

const CUSTOM_FIELDS = [
  { key: "sect", title: "Section header", icon: "fa-header" },
  { key: "str", title: "Short text", icon: "fa-font" },
  { key: "text", title: "Long text", icon: "fa-align-left" },
  { key: "bool", title: "Yes / No", icon: "fa-check-square-o" },
  { key: "select", title: "Multiple choice", icon: "fa-list-ul" },
  { key: "num", title: "Number", icon: "fa-hashtag" },
  { key: "signature", title: "Signature", icon: "fa-pencil" },
] as const;

export const FORM_PALETTE = { common: COMMON_FIELDS, custom: CUSTOM_FIELDS };

function covidScreening(): FormElem[] {
  return [
    {
      id: "s1",
      type: "sect",
      name: "Patient information",
      children: [
        {
          id: "f1",
          type: "str",
          placeholder: "Full name",
          required: true,
        },
        {
          id: "f2",
          type: "date",
          placeholder: "Date of birth",
          required: true,
        },
      ],
    },
    {
      id: "s2",
      type: "sect",
      name: "Screening questions",
      desc: "Please answer based on the last 14 days.",
      children: [
        {
          id: "f3",
          type: "bool",
          name: "Have you had a fever in the last 14 days?",
          required: true,
        },
        {
          id: "f4",
          type: "bool",
          name: "Have you traveled internationally in the last 14 days?",
        },
        {
          id: "f5",
          type: "select",
          name: "Please describe your symptoms",
          options: [
            { val: "cough", dispval: "Cough" },
            { val: "fever", dispval: "Fever" },
            { val: "breath", dispval: "Shortness of breath" },
            { val: "none", dispval: "None of the above" },
          ],
        },
        {
          id: "f6",
          type: "num",
          placeholder: "Temperature",
          unit: "°F",
        },
        {
          id: "f7",
          type: "text",
          placeholder: "Anything else we should know?",
        },
      ],
    },
    {
      id: "s3",
      type: "sect",
      name: "Acknowledgement",
      children: [
        {
          id: "f8",
          type: "signature",
          name: "I certify this information is accurate",
          desc: "By agreeing you confirm the answers above are true to the best of your knowledge.",
          required: true,
        },
      ],
    },
  ];
}

function intake(): FormElem[] {
  return [
    {
      id: "s1",
      type: "sect",
      name: "About you",
      children: [
        { id: "f1", type: "str", placeholder: "Full name", required: true },
        { id: "f2", type: "date", placeholder: "Date of birth", required: true },
        { id: "f3", type: "str", placeholder: "Email" },
        { id: "f4", type: "str", placeholder: "Phone" },
      ],
    },
    {
      id: "s2",
      type: "sect",
      name: "Insurance",
      children: [
        { id: "f5", type: "str", placeholder: "Insurance carrier" },
        { id: "f6", type: "str", placeholder: "Member ID" },
        {
          id: "f7",
          type: "str",
          placeholder: "Emergency contact",
        },
      ],
    },
    {
      id: "s3",
      type: "sect",
      name: "Consent",
      children: [
        {
          id: "f8",
          type: "signature",
          name: "I agree to the practice policies",
          required: true,
        },
      ],
    },
  ];
}

const DOCUMENTS: Record<string, FormDocument> = {
  archive_form_01: {
    name: "New patient intake",
    desc: "Collected before a first visit so the front desk has contact, insurance, and consent on file.",
    role: "Intake",
    parent: "Cobble Hill Family Dental (Location)",
    children: intake(),
  },
  archive_form_02: {
    name: "Medical history update",
    desc: "Annual update of medications, allergies, and conditions.",
    role: "Clinical",
    parent: "Cobble Hill Family Dental (Location)",
    children: [
      {
        id: "s1",
        type: "sect",
        name: "Medical history",
        children: [
          {
            id: "f1",
            type: "text",
            placeholder: "Current medications",
          },
          {
            id: "f2",
            type: "text",
            placeholder: "Allergies",
          },
          {
            id: "f3",
            type: "bool",
            name: "Have you been hospitalized in the last year?",
          },
        ],
      },
    ],
  },
  archive_form_03: {
    name: "COVID / health screening",
    desc: "Day-of-visit screening for fever, travel, and respiratory symptoms.",
    role: "Screening",
    parent: "Cobble Hill Family Dental (Location)",
    children: covidScreening(),
  },
  archive_form_04: {
    name: "Pediatric guardian consent",
    desc: "Guardian consent for treatment of a minor patient.",
    role: "Consent",
    parent: "Cobble Hill Family Dental (Location)",
    children: [
      {
        id: "s1",
        type: "sect",
        name: "Guardian",
        children: [
          { id: "f1", type: "str", placeholder: "Guardian full name" },
          { id: "f2", type: "str", placeholder: "Relationship to patient" },
          {
            id: "f3",
            type: "signature",
            name: "I consent to treatment",
            required: true,
          },
        ],
      },
    ],
  },
  archive_form_05: {
    name: "Ortho interest questionnaire",
    desc: "Short interest form for cosmetic and orthodontic consults.",
    role: "Marketing",
    parent: "Cobble Hill Family Dental (Location)",
    children: [
      {
        id: "s1",
        type: "sect",
        name: "Interest",
        children: [
          {
            id: "f1",
            type: "select",
            name: "What are you interested in?",
            options: [
              { val: "whitening", dispval: "Whitening" },
              { val: "aligners", dispval: "Clear aligners" },
              { val: "braces", dispval: "Braces" },
            ],
          },
          {
            id: "f2",
            type: "text",
            placeholder: "Anything you want the doctor to know?",
          },
        ],
      },
    ],
  },
};

export function documentForForm(id: string | undefined, fallbackName: string): FormDocument {
  if (id && DOCUMENTS[id]) return DOCUMENTS[id];
  return {
    name: fallbackName,
    desc: "",
    role: "Intake",
    parent: "Cobble Hill Family Dental (Location)",
    children: intake(),
  };
}

export function flattenNames(elems: FormElem[]): { id: string; label: string; depth: number }[] {
  const out: { id: string; label: string; depth: number }[] = [];
  const walk = (nodes: FormElem[], depth: number) => {
    for (const node of nodes) {
      out.push({
        id: node.id,
        label: node.name || node.placeholder || node.type,
        depth,
      });
      if (node.children?.length) walk(node.children, depth + 1);
    }
  };
  walk(elems, 0);
  return out;
}
